/**
 * The app mark — **語, set live**, on its vermilion field.
 *
 * ## Why it is type again
 *
 * It was an `<img>`, and the reason was explicit: rendering the mark live would put Noto Sans JP
 * 900 in the shell's critical path for one 32px element with no state that never recolours and
 * never scales — a webfont bought for nothing. That argument was correct and it is now spent. The
 * theme loads Noto Sans JP for every Japanese character in the product, so the face is already
 * there; keeping a PNG would mean shipping an image of a glyph the browser has.
 *
 * What that buys, beyond one fewer request: the mark takes the **in-app radius** (2px, sharing
 * geometry with every panel and chip around it) where the artwork's corner is baked in at the
 * rounded OS-icon radius, and the sub-20px switch to 言 becomes a rendering rule rather than a
 * second asset. `docs/ICONS.md` asks for "one new asset: the sub-20px 言 for in-app use" — with
 * live type there is nothing to cut.
 *
 * The PNG family still ships. It is what a favicon, a home screen, a task bar and an install
 * prompt need, and none of those can be type.
 *
 * ## The two rules the artwork encoded, kept
 *
 * **語 has fourteen strokes and fuses below 20px**, so the mark switches to 言 — its own radical,
 * which keeps the silhouette family and the first stroke. The delivered family already did this at
 * the 16px favicon; here it is a size comparison rather than a different file.
 *
 * **CJK ink sits below the centre of its line box** — Noto Sans JP's ascent is 1.16em against a
 * 0.288em descent — so a glyph centred with `align-items: center` and `line-height: 1` renders
 * visibly low. `ICONS.md` gives the correction per glyph as an em-based translate, which is what
 * makes one value hold at every size: 語 is `-0.07em`, 言 is `-0.125em`. They differ because 言 is
 * the top half of 語 and sits higher in the box to begin with.
 */
export function Mark({ size = 26, className = "" }: { size?: number; className?: string }) {
  // The boundary ICONS.md sets, applied to the rendered size rather than to a file name.
  const simplified = size < 20;
  return (
    <span
      aria-hidden
      // `checks/` hook. The mark is `aria-hidden` — it is the app's identity, not a label — so a
      // check has no accessible name to find it by, and `check-mobile.mjs` asserts exactly where
      // it does and does not appear.
      data-check="mark"
      className={`flex shrink-0 items-center justify-center rounded-[var(--r-2)] bg-accent-mark ${className}`}
      style={{ width: size, height: size }}
    >
      <span
        className="block font-jp font-black text-ink"
        style={{
          // ~0.69 of the tile, which is the ratio the delivered artwork uses: the glyph fills the
          // field rather than sitting in it.
          fontSize: Math.round(size * 0.69),
          lineHeight: 1,
          transform: `translateY(${simplified ? "-0.125em" : "-0.07em"})`,
        }}
      >
        {simplified ? "言" : "語"}
      </span>
    </span>
  );
}
